import { useState, useEffect } from 'react';
import { getJudgingDataForCategory } from 'services/vistaJudging';

interface Aspect {
  name: string;
  weight: number;
  scaleGuidance: Record<string, string>;
}

interface JudgingData {
  aspects: Aspect[];
}

interface UseCategoryJudgingDataReturn {
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  categoryJudgingData: Record<string, JudgingData>;
  loadingCriteria: boolean;
  initialLoading: boolean;
}

const useCategoryJudgingData = (
  availableCategories: string[],
  year: number
): UseCategoryJudgingDataReturn => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categoryJudgingData, setCategoryJudgingData] = useState<
    Record<string, JudgingData>
  >({});
  const [loadingCriteria, setLoadingCriteria] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const fetchJudgingDataForCategory = async (
    category: string
  ): Promise<JudgingData> => {
    try {
      const judgingData = await getJudgingDataForCategory(category, year);
      const aspects: Aspect[] = (judgingData.aspects || []).map((a: any) => ({
        name: a.name,
        weight: a.weight,
        scaleGuidance: a.scale_guidance ?? {},
      }));
      const data: JudgingData = { aspects };
      setCategoryJudgingData(prev => ({ ...prev, [category]: data }));
      return data;
    } catch (error) {
      console.error(`Error fetching judging data for ${category}:`, error);
      throw error;
    }
  };

  useEffect(() => {
    const prefetchAllCategories = async () => {
      if (availableCategories.length > 0) {
        setLoadingCriteria(true);
        try {
          await Promise.all(
            availableCategories.map(category =>
              fetchJudgingDataForCategory(category)
            )
          );
          if (!selectedCategory) {
            setSelectedCategory(availableCategories[0]);
          }
        } catch (error) {
          console.error('Error prefetching category data:', error);
          if (!selectedCategory) {
            setSelectedCategory(availableCategories[0]);
          }
        } finally {
          setLoadingCriteria(false);
          setInitialLoading(false);
        }
      }
    };

    prefetchAllCategories();
  }, [availableCategories.length]);

  return {
    selectedCategory,
    setSelectedCategory,
    categoryJudgingData,
    loadingCriteria,
    initialLoading,
  };
};

export default useCategoryJudgingData;
